import { Gamepad2 } from 'lucide-react';
import { catalog } from '../lib/catalog';
import { deviceSearchText, normalizeDeviceName } from '../lib/deviceNames';
import { DeviceIcon } from './DeviceIcon';
import { SearchablePicker, type PickerOption } from './SearchablePicker';

const MANUFACTURERS = ['Nintendo', 'Sony', 'Sega', 'Microsoft', 'Atari', 'Analogue', 'NEC', 'SNK'];

function manufacturer(device: string): string {
  if (device.startsWith('Super Nintendo ')) return 'Nintendo';
  return MANUFACTURERS.find((maker) => device.startsWith(`${maker} `)) ?? 'Other devices & collections';
}

const profileCounts = new Map<string, number>();
for (const profile of catalog.profiles) {
  profileCounts.set(profile.device, (profileCounts.get(profile.device) ?? 0) + 1);
}

const deviceOptions: PickerOption[] = [
  { value: '', label: 'All devices', icon: <Gamepad2 size={24} />, count: catalog.profileCount },
  ...[...MANUFACTURERS, 'Other devices & collections'].flatMap((group) => catalog.devices
    .filter((device) => manufacturer(device) === group)
    .sort((left, right) => left.replace(/^_+/, '').localeCompare(right.replace(/^_+/, ''), undefined, { numeric: true }))
    .map((device) => ({
      value: device,
      label: device.replace(/^_+/, ''),
      searchText: deviceSearchText(device),
      icon: <DeviceIcon device={device} compact />,
      count: profileCounts.get(device) ?? 0,
      group,
    }))),
];

interface DevicePickerProps {
  value: string;
  onChange: (device: string) => void;
  label?: string;
  includeInstructions?: boolean;
}

export function DevicePicker({ value, onChange, label = 'Filter popular profiles by console or device', includeInstructions = false }: DevicePickerProps) {
  return (
    <SearchablePicker
      value={normalizeDeviceName(value)}
      onChange={onChange}
      options={includeInstructions ? deviceOptions : deviceOptions.filter((option) => option.value !== '__Profile Instructions')}
      label={label}
      dialogLabel="Choose console or device"
      searchLabel="Find a console or device"
      emptyMessage="No devices found. Try another name."
    />
  );
}
